// pages/W_HR_R450.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import DataGrid, {
  Column,
  Paging,
  FilterRow,
  HeaderFilter,
  SearchPanel,
  Sorting,
  Export,  
} from "devextreme-react/data-grid";
import SelectBox from "devextreme-react/select-box";
import TextBox from "devextreme-react/text-box";
import CheckBox from "devextreme-react/check-box";
import { Button } from 'devextreme-react/button';
import 'devextreme/dist/css/dx.material.blue.light.css';
import ExcelJS from "exceljs";
import saveAs from "file-saver";
import { exportDataGrid } from 'devextreme/excel_exporter';
import API_URL from '../config';

export default function W_HR_R450({ user }) {
  const [custList, setCustList] = useState([]);
  const [selectedCust, setSelectedCust] = useState(null);
  const [lotNo, setLotNo] = useState("");
  const [lotSub, setLotSub] = useState("");
  const [chkDel, setChkDel] = useState(false);
  const [data, setData] = useState([]);
  const [comboLoading, setComboLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      setComboLoading(true);
      setError("");
      try {
        const res = await axios.get(`${API_URL}/api/oracle/cust`);
        if (res.data.retCode !== 0) throw new Error(res.data.retMsg);
        setCustList(res.data.data || []);
      } catch (e) {
        setError(`CUST 로드 오류: ${e.message}`);
      } finally {
        setComboLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (custList.length > 0 && user?.role === "CUST") {
      const code = user.username.trim().toUpperCase();
      const found = custList.find(item => item.CODE_CD.trim().toUpperCase() === code);
      if (found) {
        setSelectedCust(found.CODE_CD);
      } else {
        setError(`사용자 코드(${user.username})가 목록에 없습니다.`);
      }
    }
  }, [custList, user]);

  const fetchData = async () => {
    if (!selectedCust) {
      setError("Please select CUST first.");
      return;
    }
    setLoading(true);
    setError("");
    setData([]);

    try {
      const params = {
        inLOTNO: lotNo || '',
        inLOTNOSUB: lotSub || '',
        inCUSTCD: selectedCust,
        inCHKDEL: chkDel ? "1" : "0"
      };
      const res = await axios.get(`${API_URL}/api/oracle/W_HR_R450`, { params });
      
      if (res.data.retCode !== 0) {
        throw new Error(res.data.retMsg);
      }
      
      const mappedData = (res.data.data || []).map((row, idx) => ({
        ...row,
        id: `${row.TRNX_CD}-${row.SEQ}-${idx}`,
      }));
      setData(mappedData);
    } catch (e) {
      const errorMessage = e.response?.data?.retMsg || e.message || "알 수 없는 오류가 발생했습니다.";
      setError(`조회 오류: ${errorMessage}`);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const onExporting = (e) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("PKG_LOT_History");
    exportDataGrid({ component: e.component, worksheet, autoFilterEnabled: true }).then(() => {
      workbook.xlsx.writeBuffer().then(buffer => {
        saveAs(new Blob([buffer], { type: "application/octet-stream" }), "PKG_Lot_History.xlsx");
      });
    });
    e.cancel = true;
  };

  return (
    <div style={{ padding: 0, fontFamily: 'Arial, sans-serif', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* 1. 검색 영역 */}
      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ marginBottom: '20px' }}>View PKG LOT HISTORY</h3>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 15, alignItems: "center" }}>
          <SelectBox
              dataSource={custList}
              value={selectedCust}
              valueExpr="CODE_CD"
              displayExpr="CODE_CD"
              onValueChanged={e => setSelectedCust(e.value)}
              disabled={comboLoading || user?.role === "CUST"}
              placeholder="Select CUST"
              searchEnabled
              showClearButton={user?.role !== "CUST"}
              label="CUST"
          />
          <TextBox value={lotNo} onValueChanged={e => setLotNo(e.value)} placeholder="LOT NO" label="LOT NO" />
          <TextBox value={lotSub} onValueChanged={e => setLotSub(e.value)} placeholder="LOT SUB" label="LOT SUB" />
          <CheckBox checked={chkDel} onValueChanged={e => setChkDel(e.value)} text="Include deletion history" />
          <Button text="Search" type="default" icon="search" onClick={fetchData} disabled={loading} />
        </div>
        {error && <p style={{ color: "red", fontWeight: 'bold' }}>{error}</p>}
      </div>

      {/* 2. 그리드 영역 (남은 공간을 모두 차지) */}
      <div style={{ flex: 1, position: 'relative' }}> {/* [수정] position: 'relative' 추가 */}
        {/* 로딩 인디케이터를 그리드 영역 안으로 이동 */}
        {loading && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <div className="loading-text">Loading...</div>
          </div>
        )}
        <DataGrid
            dataSource={data}
            keyExpr="id"
            onExporting={onExporting}
            height="100%"
            showBorders={true}
            columnAutoWidth={true}
            allowColumnResizing={true}
            hoverStateEnabled={true}
        >
            <Export enabled={true} allowExportSelectedData={false} />
            <Paging defaultPageSize={10} />
            <FilterRow visible={true} />
            <HeaderFilter visible={true} />
            <SearchPanel visible={true} highlightCaseSensitive={false} />
            <Sorting mode="multiple" />

            <Column dataField="TRNX_CD" caption="TRNX CD" fixed={true}/>
            <Column dataField="SEQ" caption="SEQ" dataType="number" fixed={true}/>
            <Column dataField="TRNX_TM" caption="TRNX TIME" fixed={true}/>
            <Column dataField="OPER_FROM" caption="OPER FROM" fixed={true} />
            <Column dataField="OPER_TO" caption="OPER TO" fixed={true} />
            <Column dataField="QTY_FROM" caption="QTY FROM" dataType="number" alignment="right" fixed={true} />
            <Column dataField="QTY_TO" caption="QTY TO" dataType="number" alignment="right" />
            <Column dataField="REJECT" caption="REJECT" dataType="number" alignment="right" />
            <Column dataField="CVQ" caption="CVQ" dataType="number" alignment="right" />
            <Column dataField="CON" caption="CON" dataType="number" alignment="right" />
            <Column dataField="SPLT_IN" caption="SPLIT IN" />
            <Column dataField="SPLT_OUT" caption="SPLIT OUT" />
            <Column dataField="MERG_IN" caption="MERGE IN" />
            <Column dataField="MERG_OUT" caption="MERGE OUT" />
            <Column dataField="CUST_FROM" caption="CUST FROM" />
            <Column dataField="PKG_FROM" caption="PKG FROM" />
            <Column dataField="LEAD_FROM" caption="LEAD FROM" />
            <Column dataField="DEVI_FROM" caption="DEVICE FROM" />
            <Column dataField="CUST_TO" caption="CUST TO" />
            <Column dataField="PKG_TO" caption="PKG TO" />
            <Column dataField="LEAD_TO" caption="LEAD TO" />
            <Column dataField="DEVI_TO" caption="DEVICE TO" />
            <Column dataField="SPLT_IN_LOT" caption="SPLIT IN LOT" />
            <Column dataField="SPLT_IN_SUB" caption="SPLIT IN SUB" />
            <Column dataField="SPLT_IN_DUP" caption="SPLIT IN DUP" />
            <Column dataField="SPLT_OT_LOT" caption="SPLIT OUT LOT" />
            <Column dataField="SPLT_OT_SUB" caption="SPLIT OUT SUB" />
            <Column dataField="SPLT_OT_DUP" caption="SPLIT OUT DUP" />
            <Column dataField="MERG_IN_LOT" caption="MERGE IN LOT" />
            <Column dataField="MERG_IN_SUB" caption="MERGE IN SUB" />
            <Column dataField="MERG_IN_DUP" caption="MERGE IN DUP" />
            <Column dataField="MERG_OT_LOT" caption="MERGE OUT LOT" />
            <Column dataField="MERG_OT_SUB" caption="MERGE OUT SUB" />
            <Column dataField="MERG_OT_DUP" caption="MERGE OUT DUP" />
            <Column dataField="HOLD_CD" caption="HOLD CODE" />
            <Column dataField="EQIP" caption="EQUIPMENT" />
            <Column dataField="OPERATORS" caption="OPERATORS" />
            <Column dataField="DEL" caption="DEL" />
            <Column dataField="DEL_SEQ" caption="DEL SEQ" dataType="number" />
            <Column dataField="DEL_TM" caption="DEL TIME"  />
            <Column dataField="CREATE_TM" caption="CREATE TIME"  />
            <Column dataField="CREATE_USER" caption="CREATE USER" />
        </DataGrid>
      </div>
    </div>
  );
}